import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import PouchDB from 'pouchdb';
import PouchFind from 'pouchdb-find';
import {
    FaSpinner,
    FaCheckCircle,
    FaTimesCircle,
    FaInfoCircle,
    FaWifi,
    FaDatabase,
    FaSync,
    FaTrashAlt
} from 'react-icons/fa';
import Button from '../../components/CustomButton/CustomButton';
import { downloadOfflineData } from '../../redux/actions/downloadOfflineData';
import { getLocalProducts, preloadProductsByIds, forceSyncFromPouchDB } from '../../redux/actions/local';
import {
    fullMongoDBSync,
    syncProductsFromMongoDB,
    syncSubstancesFromMongoDB,
    syncTrainingsFromFirebase,
    syncProfilesFromFirebase,
    getSyncStats,
    clearOfflineData
} from '../../redux/actions/mongoOfflineSync';
import OfflineStorageManager from '../../helpers/offlineStorageManager';

// Installation du plugin find
PouchDB.plugin(PouchFind);

// Ajout des styles CSS pour les animations
const addAnimationStyles = () => {
    if (!document.getElementById('offline-animations')) {
        const style = document.createElement('style');
        style.id = 'offline-animations';
        style.textContent = `
            .spin {
                animation: spin 1s linear infinite;
            }
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
    }
};

const styles = {
    container: {
        maxWidth: '1480px',
        margin: '0 auto',
        padding: '28px 32px 48px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        color: '#263746'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px',
        padding: '24px 28px',
        background: 'linear-gradient(120deg, #ffffff 0%, #f2fbfd 100%)',
        border: '1px solid #e4eef1',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(31, 58, 69, 0.05)'
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        color: '#17313d',
        gap: '8px'
    },
    onlineStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        background: '#ecfdf5',
        border: '1px solid #bbf7d0',
        borderRadius: '999px',
        fontSize: '13px',
        fontWeight: 600,
        color: '#15803d'
    },
    offlineStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '999px',
        fontSize: '13px',
        fontWeight: 600,
        color: '#b91c1c'
    },
    progressContainer: {
        margin: '20px 0',
        padding: '18px 20px',
        backgroundColor: '#fff',
        border: '1px solid #e4eef1',
        borderRadius: '12px'
    },
    progressBar: {
        width: '100%',
        height: '8px',
        backgroundColor: '#e8eff2',
        borderRadius: '999px',
        overflow: 'hidden',
        marginBottom: '10px'
    },
    progressFill: {
        height: '100%',
        backgroundColor: 'var(--app-primary-color)',
        borderRadius: '999px',
        transition: 'width 0.3s ease'
    },
    statusCard: {
        padding: '18px 20px',
        backgroundColor: '#fff',
        border: '1px solid #e4eef1',
        borderRadius: '12px',
        boxShadow: '0 5px 18px rgba(31, 58, 69, 0.04)',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    successIcon: {
        fontSize: '24px',
        color: '#28a745',
        marginRight: '15px'
    },
    warningIcon: {
        fontSize: '24px',
        color: '#ffc107',
        marginRight: '15px'
    },
    statusText: {
        flex: 1
    },
    errorCard: {
        padding: '18px 20px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 5px 18px rgba(31, 58, 69, 0.04)',
        border: '1px solid #fecaca',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        borderLeft: '4px solid #dc3545'
    },
    errorIcon: {
        fontSize: '24px',
        color: '#dc3545',
        marginRight: '15px'
    },
    errorText: {
        flex: 1
    },
    errorTitle: {
        margin: '0 0 5px 0',
        color: '#dc3545',
        fontSize: '16px',
        fontWeight: 'bold'
    },
    button: {
        margin: '5px',
        padding: '11px 18px',
        borderRadius: '9px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        transition: 'all 0.2s ease'
    }
};

class Offline extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            downloadProgress: 0,
            lastSync: null,
            error: null,
            dataSize: 'Calcul...',
            isOnline: navigator.onLine,
            hasData: false,
            isSyncing: false,
            currentSyncStep: null,
            totalElements: 0,
            downloadedElements: 0
        };
    }

    componentDidMount() {
        this.initPouchDB();
        this.updateStorageInfo();
        window.addEventListener('online', this.handleNetworkChange.bind(this));
        window.addEventListener('offline', this.handleNetworkChange.bind(this));
        addAnimationStyles();
    }

    componentWillUnmount() {
        window.removeEventListener('online', this.handleNetworkChange.bind(this));
        window.removeEventListener('offline', this.handleNetworkChange.bind(this));
    }

    initPouchDB() {
        // Initialisation PouchDB basique
    }

    handleNetworkChange() {
        this.setState({ isOnline: navigator.onLine });
    }

    formatBytes(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    // Fonction principale pour télécharger toutes les données
    async handleDownloadAllData() {
        if (this.state.isSyncing) {
            return;
        }

        this.setState({
            isSyncing: true,
            error: null,
            downloadProgress: 0,
            currentSyncStep: 'Téléchargement en cours...',
            totalElements: 0,
            downloadedElements: 0
        });

        try {
            await this.props.downloadOfflineData((progress) => {
                const percentage = progress.progress || 0;
                const downloaded = progress.current || progress.downloaded || 0;
                const total = progress.total || 0;

                this.setState({
                    downloadProgress: Math.min(percentage, 100),
                    currentSyncStep: progress.message || `Téléchargement... ${progress.phase || ''}`,
                    downloadedElements: downloaded,
                    totalElements: total
                });
            });


            this.setState({
                hasData: true,
                error: null,
                isSyncing: false,
                downloadProgress: 100,
                currentSyncStep: 'Téléchargement terminé avec succès !',
                lastSync: new Date().toLocaleString()
            });

            // Sauvegarder le timestamp de synchronisation
            localStorage.setItem('last_offline_sync', new Date().toISOString());

            // Mettre à jour les informations de stockage
            await this.updateStorageInfo();

        } catch (error) {
            let errorMessage = 'Erreur inconnue';
            if (error.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }

            this.setState({
                error: `Erreur de téléchargement: ${errorMessage}`,
                isSyncing: false,
                downloadProgress: 0,
                currentSyncStep: 'Échec du téléchargement'
            });
        } finally {
            this.setState({
                isSyncing: false,
                currentSyncStep: null
            });
        }
    }

    async handleClearData() {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer toutes les données locales ?')) {
            return;
        }

        try {
            this.setState({
                isSyncing: true,
                currentSyncStep: 'Suppression des données en cours...'
            });

            // Utiliser le gestionnaire de stockage pour nettoyer
            const cleared = await OfflineStorageManager.clearAllOfflineData();

            if (cleared) {
                // Également appeler l'action Redux pour nettoyer
                await this.props.clearOfflineData();

                this.setState({
                    hasData: false,
                    lastSync: null,
                    error: null,
                    dataSize: '0 Bytes'
                });

                // Mettre à jour les informations de stockage
                await this.updateStorageInfo();
            } else {
                this.setState({
                    error: 'Erreur lors de la suppression de certaines données'
                });
            }
        } catch (error) {
            this.setState({
                error: `Erreur lors de la suppression: ${error.message}`
            });
        } finally {
            this.setState({
                isSyncing: false,
                currentSyncStep: null
            });
        }
    }

    async updateStorageInfo() {
        try {
            const status = await OfflineStorageManager.getOfflineStatus();

            this.setState({
                dataSize: status.totalSize,
                hasData: Object.keys(status.databases).some(db =>
                    status.databases[db].docCount > 0
                ),
                lastSync: status.lastSync ? new Date(status.lastSync).toLocaleString() : null
            });
        } catch (error) {
            this.setState({ dataSize: 'Erreur calcul' });
        }
    }

    renderStatsGrid() {
        // Statistiques cachées pour les clients
        return null;
    }

    render() {
        const { loading } = this.props;
        const {
            downloadProgress,
            lastSync,
            error,
            isOnline,
            hasData,
            isSyncing,
            currentSyncStep,
            downloadedElements,
            totalElements
        } = this.state;

        return (
            <div className="offline-page" style={styles.container}>
                {/* En-tête avec statut de connexion */}
                <div className="offline-page-header" style={styles.header}>
                    <div style={styles.title}>
                        <FaDatabase style={{ marginRight: '10px', fontSize: '24px' }} />
                        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700 }}>Données Hors Ligne</h2>
                    </div>
                    <div style={isOnline ? styles.onlineStatus : styles.offlineStatus}>
                        <FaWifi style={{ marginRight: '5px' }} />
                        {isOnline ? 'En ligne' : 'Hors ligne'}
                    </div>
                </div>

                {/* Barre de progression */}
                {isSyncing && (
                    <div style={styles.progressContainer}>
                        <div style={styles.progressBar}>
                            <div style={{ ...styles.progressFill, width: `${downloadProgress}%` }} />
                        </div>
                        <div>Progression: {Math.round(downloadProgress)}%</div>
                    </div>
                )}

                {/* Status de synchronisation terminée */}
                {hasData && !isSyncing && lastSync && (
                    <div style={styles.statusCard}>
                        <div style={styles.successIcon}>
                            <FaCheckCircle />
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 5px 0', color: '#28a745' }}>
                                Données synchronisées
                            </h4>
                            <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
                                Dernière synchronisation: {lastSync}
                            </p>
                        </div>
                    </div>
                )}

                {/* Affichage d'erreur */}
                {error && (
                    <div style={styles.errorCard}>
                        <div style={styles.errorIcon}>
                            <FaTimesCircle />
                        </div>
                        <div style={styles.errorText}>
                            <h3 style={styles.errorTitle}>Erreur</h3>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                {/* Section de progression détaillée */}
                {currentSyncStep && (
                    <div style={{...styles.statusCard, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7'}}>
                        <div style={{...styles.warningIcon, color: '#e17055'}}>
                            <FaSync className="spin" />
                        </div>
                        <div style={styles.statusText}>
                            <h4 style={{margin: '0 0 8px 0', color: '#2d3436'}}>
                                {currentSyncStep}
                            </h4>
                            <p style={{margin: 0, fontSize: '14px', color: '#636e72'}}>
                                {downloadedElements > 0 && `${downloadedElements} éléments téléchargés sur ${totalElements}`}
                            </p>
                        </div>
                    </div>
                )}

                {/* Boutons d'action */}
                <div className="offline-actions" style={{display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px'}}>
                    <Button
                        bsStyle="success"
                        bsSize="lg"
                        fill
                        className="offline-action-primary"
                        style={{...styles.button, flex: '1', minWidth: '250px', backgroundColor: 'var(--app-primary-color)', color: '#073b46'}}
                        onClick={this.handleDownloadAllData.bind(this)}
                        disabled={loading || !isOnline || isSyncing}
                    >
                        {isSyncing ? (
                            <>
                                <FaSpinner className="spin" style={{ marginRight: 10 }} />
                                Téléchargement...
                            </>
                        ) : hasData ? (
                            <>
                                <FaSync style={{ marginRight: 10 }} />
                                Resynchroniser toutes les données
                            </>
                        ) : (
                            <>
                                <FaDatabase style={{ marginRight: 10 }} />
                                Télécharger toutes les données
                            </>
                        )}
                    </Button>

                    <Button
                        bsStyle="danger"
                        bsSize="lg"
                        fill
                        className="offline-action-danger"
                        style={{...styles.button, minWidth: '200px', backgroundColor: '#fff', color: '#b42318', border: '1px solid #f0b8b4'}}
                        onClick={this.handleClearData.bind(this)}
                        disabled={loading || isSyncing}
                    >
                        <FaTimesCircle style={{ marginRight: 10 }} />
                        Supprimer les données locales
                    </Button>
                </div>

                {/* Informations sur les données stockées */}
                <div style={styles.statusCard}>
                    <div style={{ ...styles.successIcon, color: '#6c757d' }}>
                        <FaInfoCircle />
                    </div>
                    <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>
                            Informations sur les données locales
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                            <div>
                                <strong>Taille totale:</strong> {this.state.dataSize}
                            </div>
                            <div>
                                <strong>Statut:</strong> {hasData ? 'Données disponibles' : 'Aucune donnée'}
                            </div>
                            {lastSync && (
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <strong>Dernière synchronisation:</strong> {lastSync}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Statistiques cachées pour les clients */}
                {this.renderStatsGrid()}
                <style>{`
                    .offline-page .offline-action-primary:hover:not(:disabled) {
                        box-shadow: 0 7px 18px rgba(var(--app-primary-rgb), 0.24);
                        transform: translateY(-1px);
                    }
                    .offline-page .offline-action-danger:hover:not(:disabled) {
                        background: #fff5f4 !important;
                        border-color: #e6a09a !important;
                    }
                    .offline-page button:focus-visible {
                        outline: 3px solid rgba(var(--app-primary-rgb), 0.28);
                        outline-offset: 2px;
                    }
                    @media (max-width: 640px) {
                        .offline-page { padding: 18px 14px 32px !important; }
                        .offline-page-header { align-items: flex-start !important; flex-direction: column; padding: 20px !important; }
                        .offline-actions { flex-direction: column; }
                        .offline-actions > button { width: 100%; min-width: 0 !important; }
                    }
                `}</style>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    loading: state.status?.loading || false
});

const mapDispatchToProps = (dispatch) => ({
    downloadOfflineData: (onProgress) => dispatch(downloadOfflineData(onProgress)),
    getLocalProducts: () => dispatch(getLocalProducts()),
    forceSyncFromPouchDB: () => dispatch(forceSyncFromPouchDB()),
    preloadProductsByIds: (ids) => dispatch(preloadProductsByIds(ids)),
    fullMongoDBSync: (onProgress) => dispatch(fullMongoDBSync(onProgress)),
    syncProductsFromMongoDB: (onProgress) => dispatch(syncProductsFromMongoDB(onProgress)),
    syncSubstancesFromMongoDB: (onProgress) => dispatch(syncSubstancesFromMongoDB(onProgress)),
    syncTrainingsFromFirebase: (onProgress) => dispatch(syncTrainingsFromFirebase(onProgress)),
    syncProfilesFromFirebase: (onProgress) => dispatch(syncProfilesFromFirebase(onProgress)),
    getSyncStats: () => dispatch(getSyncStats()),
    clearOfflineData: () => dispatch(clearOfflineData())
});

Offline.propTypes = {
    loading: PropTypes.bool,
    downloadOfflineData: PropTypes.func.isRequired,
    getLocalProducts: PropTypes.func.isRequired,
    forceSyncFromPouchDB: PropTypes.func.isRequired,
    preloadProductsByIds: PropTypes.func.isRequired,
    fullMongoDBSync: PropTypes.func.isRequired,
    syncProductsFromMongoDB: PropTypes.func.isRequired,
    syncSubstancesFromMongoDB: PropTypes.func.isRequired,
    syncTrainingsFromFirebase: PropTypes.func.isRequired,
    syncProfilesFromFirebase: PropTypes.func.isRequired,
    getSyncStats: PropTypes.func.isRequired,
    clearOfflineData: PropTypes.func.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(Offline);
